(() => {
  const navigationEntry = performance.getEntriesByType('navigation')[0];
  const isReloadNavigation = navigationEntry?.type === 'reload';

  if (isReloadNavigation && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  if (isReloadNavigation && location.hash) {
    history.replaceState(history.state, '', `${location.pathname}${location.search}`);
  }

  const restoreReloadTop = () => {
    if (!isReloadNavigation) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  restoreReloadTop();

  if (isReloadNavigation) {
    window.addEventListener('pageshow', restoreReloadTop, { once: true });
    window.addEventListener(
      'load',
      () => window.requestAnimationFrame(() => window.requestAnimationFrame(restoreReloadTop)),
      { once: true },
    );
  }

  const SELECTOR = '[data-deeb-about-root]';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();

  class SeamlessRail {
    constructor(root, options = {}) {
      this.root = root;
      this.track = root?.querySelector('[data-deeb-rail-track]');
      this.groups = [...(root?.querySelectorAll('[data-deeb-rail-group]') || [])];
      this.baseSpeed = Number(options.speed || root?.dataset.deebRailSpeed || 42);
      this.interactive = root?.dataset.deebRailInteractive === 'true';
      this.offset = 0;
      this.cycleWidth = 0;
      this.last = performance.now();
      this.raf = 0;
      this.paused = true;
      this.inView = false;
      this.dragging = false;
      this.pointerId = null;
      this.dragStartX = 0;
      this.dragStartOffset = 0;
      this.holdUntil = 0;
      this.visibilityObserver = null;
      this.resizeObserver = null;
      this.runtimeClones = [];
      this.onResize = this.measure.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.tick = this.tick.bind(this);
      this.init();
    }

    init() {
      if (!this.root || !this.track || !this.groups.length || reduceMotion.matches) return;

      this.measure();
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);

      if (this.interactive) {
        this.root.classList.add('is-interactive');
        this.root.addEventListener('pointerdown', this.onPointerDown);
        this.root.addEventListener('pointermove', this.onPointerMove);
        this.root.addEventListener('pointerup', this.onPointerUp);
        this.root.addEventListener('pointercancel', this.onPointerUp);
        this.root.addEventListener('keydown', this.onKeyDown);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.groups.slice(0, 2).forEach((group) => this.resizeObserver.observe(group));
      }

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.inView = Boolean(entry?.isIntersecting);
            this.syncPausedState();
          },
          { rootMargin: '120px 0px', threshold: 0.01 },
        );
        this.visibilityObserver.observe(this.root);
      } else {
        this.inView = true;
        this.syncPausedState();
      }
    }

    normalize(value) {
      if (!this.cycleWidth) return 0;
      return ((value % this.cycleWidth) + this.cycleWidth) % this.cycleWidth;
    }

    applyOffset() {
      if (!this.track) return;
      this.track.style.transform = `translate3d(${-this.offset.toFixed(2)}px, 0, 0)`;
    }

    syncPausedState() {
      const nextPaused = document.hidden || !this.inView || this.dragging;
      this.paused = nextPaused;
      this.last = performance.now();
      if (!this.paused && !this.raf) this.raf = window.requestAnimationFrame(this.tick);
    }

    clearRuntimeClones() {
      this.runtimeClones.forEach((clone) => clone.remove());
      this.runtimeClones = [];
    }

    ensureCoverage() {
      if (!this.root || !this.track || !this.groups.length || !this.cycleWidth) return;

      const viewportWidth = Math.max(
        this.root.clientWidth || 0,
        this.root.getBoundingClientRect().width || 0,
      );
      const requiredWidth = this.cycleWidth + viewportWidth + 2;
      let coveredWidth = this.groups.length * this.cycleWidth;
      let safety = 0;

      while (coveredWidth < requiredWidth && safety < 12) {
        const clone = this.groups[0].cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-deeb-rail-runtime-clone', 'true');
        clone.setAttribute('inert', '');
        this.track.appendChild(clone);
        this.runtimeClones.push(clone);
        coveredWidth += this.cycleWidth;
        safety += 1;
      }
    }

    measure() {
      if (!this.groups.length) return;

      this.clearRuntimeClones();

      const first = this.groups[0];
      const second = this.groups[1];
      let measured = 0;

      if (second) {
        const firstRect = first.getBoundingClientRect();
        const secondRect = second.getBoundingClientRect();
        measured = Math.abs(secondRect.left - firstRect.left);
      }

      if (!measured) measured = first.getBoundingClientRect().width;
      this.cycleWidth = measured;

      if (this.cycleWidth > 0) {
        this.ensureCoverage();
        this.offset = this.normalize(this.offset);
        this.applyOffset();
      }
    }

    onVisibility() {
      this.syncPausedState();
    }

    onPointerDown(event) {
      if (!this.interactive || !this.cycleWidth) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.dragging = true;
      this.pointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartOffset = this.offset;
      this.holdUntil = 0;
      this.root.classList.add('is-dragging');
      this.root.setPointerCapture?.(event.pointerId);
      this.syncPausedState();
    }

    onPointerMove(event) {
      if (!this.dragging || event.pointerId !== this.pointerId || !this.cycleWidth) return;
      const deltaX = event.clientX - this.dragStartX;
      this.offset = this.normalize(this.dragStartOffset - deltaX);
      this.applyOffset();
    }

    onPointerUp(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;
      this.root.releasePointerCapture?.(event.pointerId);
      this.dragging = false;
      this.pointerId = null;
      this.root.classList.remove('is-dragging');
      this.holdUntil = performance.now() + 900;
      this.syncPausedState();
    }

    onKeyDown(event) {
      if (!this.interactive || !this.cycleWidth || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.offset = this.normalize(this.offset + direction * Math.min(180, this.cycleWidth * 0.08));
      this.holdUntil = performance.now() + 1100;
      this.applyOffset();
    }

    tick(now) {
      this.raf = 0;
      if (this.paused) return;
      if (!this.cycleWidth) this.measure();

      const delta = Math.min(48, Math.max(0, now - this.last));
      this.last = now;

      if (this.cycleWidth > 0 && now >= this.holdUntil) {
        this.offset = this.normalize(this.offset + (this.baseSpeed * delta) / 1000);
        this.applyOffset();
      }

      this.raf = window.requestAnimationFrame(this.tick);
    }

    destroy() {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.root?.removeEventListener('pointerdown', this.onPointerDown);
      this.root?.removeEventListener('pointermove', this.onPointerMove);
      this.root?.removeEventListener('pointerup', this.onPointerUp);
      this.root?.removeEventListener('pointercancel', this.onPointerUp);
      this.root?.removeEventListener('keydown', this.onKeyDown);
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.clearRuntimeClones();
      this.track?.style.removeProperty('transform');
      this.root?.classList.remove('is-interactive', 'is-dragging');
    }
  }

  class DeebAboutMotion {
    constructor(root) {
      this.root = root;
      this.hero = root.querySelector('[data-deeb-hero]');
      this.ritual = root.querySelector('[data-deeb-cigar-ritual]');
      this.cigarStage = root.querySelector('.deeb-about__cigar-stage');
      this.cigarBand = root.querySelector('.deeb-about__cigar-band');
      this.approach = root.querySelector('[data-deeb-approach]');
      this.approachRows = [...root.querySelectorAll('[data-deeb-approach-row]')];
      this.cairoTime = root.querySelector('[data-deeb-cairo-time]');
      this.ritualGuide = root.querySelector('[data-deeb-ritual-guide]');
      this.ritualTabs = [...root.querySelectorAll('[data-deeb-ritual-tab]')];
      this.ritualPanels = [...root.querySelectorAll('[data-deeb-ritual-panel]')];
      this.ritualTabHandlers = [];
      this.ritualMaxEmberX = 72;
      this.revealObserver = null;
      this.ritualVisibilityObserver = null;
      this.parallaxItems = [];
      this.rails = [];
      this.raf = 0;
      this.timeTimer = 0;
      this.pointerRaf = 0;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.init();
    }

    init() {
      this.root.classList.add('is-ready');
      this.initReveal();
      this.initCairoTime();
      this.initRitualGuide();
      this.initRitualVisibility();
      this.initRails();
      this.initMotion();
      this.initPointerMotion();
    }

    initReveal() {
      const targets = [...this.root.querySelectorAll('[data-deeb-reveal], [data-deeb-line]')];
      if (!targets.length) return;

      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        targets.forEach((target) => target.classList.add('is-visible'));
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -9% 0px', threshold: 0.11 },
      );

      targets.forEach((target) => this.revealObserver.observe(target));
    }

    initCairoTime() {
      if (!this.cairoTime) return;

      const update = () => {
        try {
          this.cairoTime.textContent = new Intl.DateTimeFormat('en-GB', {
            timeZone: 'Africa/Cairo',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          }).format(new Date());
        } catch (_) {
          this.cairoTime.textContent = '--:--:--';
        }
      };

      update();
      this.timeTimer = window.setInterval(update, 1000);
    }

    initRitualGuide() {
      if (!this.ritualGuide || !this.ritualTabs.length || !this.ritualPanels.length) return;

      const activate = (name, focus = false) => {
        this.ritualTabs.forEach((tab) => {
          const active = tab.dataset.deebRitualTab === name;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          if (active && focus) tab.focus({ preventScroll: true });
        });

        this.ritualPanels.forEach((panel) => {
          const active = panel.dataset.deebRitualPanel === name;
          panel.classList.toggle('is-active', active);
          panel.hidden = !active;
        });

        if (this.ritual) this.ritual.dataset.deebRitualMode = name;
      };

      this.ritualTabs.forEach((tab, index) => {
        const click = () => activate(tab.dataset.deebRitualTab || 'select');
        const keydown = (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const next = (index + direction + this.ritualTabs.length) % this.ritualTabs.length;
          activate(this.ritualTabs[next].dataset.deebRitualTab || 'select', true);
        };
        tab.addEventListener('click', click);
        tab.addEventListener('keydown', keydown);
        this.ritualTabHandlers.push([tab, click, keydown]);
      });

      const initialName =
        this.ritualTabs.find((tab) => tab.classList.contains('is-active'))?.dataset.deebRitualTab || 'select';

      if (this.ritual) this.ritual.dataset.deebRitualMode = initialName;
    }

    initRitualVisibility() {
      if (!this.ritual) return;

      const setActive = (active) => {
        this.ritual.classList.toggle('is-render-active', active);
      };

      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        setActive(true);
        return;
      }

      this.ritualVisibilityObserver = new IntersectionObserver(
        (entries) => {
          setActive(Boolean(entries[0]?.isIntersecting));
        },
        {
          root: null,
          rootMargin: '180px 0px 180px 0px',
          threshold: 0.01,
        },
      );

      this.ritualVisibilityObserver.observe(this.ritual);
    }

    initRails() {
      if (reduceMotion.matches) return;
      const rails = [...this.root.querySelectorAll('[data-deeb-rail]')];
      this.rails = rails.map((rail) => new SeamlessRail(rail));
    }

    initMotion() {
      if (reduceMotion.matches) return;

      this.parallaxItems = [...this.root.querySelectorAll('[data-deeb-parallax]')];

      if (!this.parallaxItems.length && !this.hero && !this.ritual && !this.approach) return;

      this.measureRitualGeometry();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.onScroll();
    }

    initPointerMotion() {
      if (reduceMotion.matches || !this.hero || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      this.hero.addEventListener('pointermove', this.onPointerMove, { passive: true });
      this.hero.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
    }

    updateHero(viewport) {
      if (!this.hero) return;

      const rect = this.hero.getBoundingClientRect();
      const heroProgress = Math.max(0, Math.min(1, (0 - rect.top) / Math.max(1, rect.height)));
      this.hero.style.setProperty('--hero-progress', heroProgress.toFixed(4));
    }

    updateParallax(viewport) {
      this.parallaxItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const distance = center - viewport / 2;
        const strength = Number(item.dataset.deebParallax || 0.045);
        const shift = Math.max(-34, Math.min(34, distance * -strength));
        item.style.setProperty('--deeb-about-parallax', shift.toFixed(2));
      });
    }

    measureRitualGeometry() {
      if (!this.cigarStage || !this.cigarBand) {
        this.ritualMaxEmberX = 72;
        this.ritualStageWidth = 0;
        return;
      }

      const stageRect = this.cigarStage.getBoundingClientRect();
      const bandRect = this.cigarBand.getBoundingClientRect();
      const stageStyle = window.getComputedStyle(this.cigarStage);

      if (stageRect.width <= 0 || bandRect.width <= 0) {
        this.ritualMaxEmberX = 72;
        this.ritualStageWidth = 0;
        return;
      }

      const leftPct = Number.parseFloat(stageStyle.getPropertyValue('--cigar-left')) || 4.5;
      const rightPct = Number.parseFloat(stageStyle.getPropertyValue('--cigar-right')) || 4.5;
      const bandLeft = ((bandRect.left - stageRect.left) / stageRect.width) * 100;
      const safetyGapPx = Math.max(24, Math.min(48, stageRect.width * 0.026));
      const safetyGap = (safetyGapPx / stageRect.width) * 100;

      this.ritualStageWidth = stageRect.width;
      this.ritualStageLeftPct = leftPct;
      this.ritualStageRightPct = rightPct;
      this.ritualMaxEmberX = Math.max(52, Math.min(82, bandLeft - safetyGap));
      this.lastRitualProgress = null;
    }

    updateRitual(viewport) {
      if (!this.ritual || !this.cigarStage) return;

      const rect = this.cigarStage.getBoundingClientRect();
      const enterY = viewport * 0.88;
      const exitY = viewport * 0.12;
      const travel = Math.max(1, enterY - exitY + rect.height);
      const progress = Math.max(0, Math.min(1, (enterY - rect.top) / travel));

      if (this.lastRitualProgress !== null && Math.abs(progress - this.lastRitualProgress) < 0.001) return;
      this.lastRitualProgress = progress;

      // Burn begins when the cigar stage itself enters the viewport. This keeps the
      // first visible frame intact instead of arriving already consumed. Geometry
      // writes use compositor-friendly translate/clip/scale variables, not left/width.
      const maxEmberX = this.ritualMaxEmberX;
      const emberX = 6.5 + progress * (maxEmberX - 6.5);
      const leftPct = this.ritualStageLeftPct || 4.5;
      const rightPct = this.ritualStageRightPct || 4.5;
      const usablePct = Math.max(1, 100 - leftPct - rightPct);
      const burnPct = Math.max(0, emberX - leftPct);
      const burnRatio = Math.max(0, Math.min(1, burnPct / usablePct));
      const stageWidth = this.ritualStageWidth || rect.width;
      const emberOffsetPx = (burnPct / 100) * stageWidth;
      const physicalBurn = Math.max(0, Math.min(0.82, (emberX - 6.5) / 87));
      const orbit = -7 + progress * 30;
      const ashOpacity = 0.32 + progress * 0.56;
      const smoke = 0.58 + progress * 0.28;

      this.ritual.style.setProperty('--cigar-ember-offset', `${emberOffsetPx.toFixed(2)}px`);
      this.ritual.style.setProperty('--cigar-body-clip', `${(burnRatio * 100).toFixed(3)}%`);
      this.ritual.style.setProperty('--cigar-ash-scale', burnRatio.toFixed(4));
      this.ritual.style.setProperty('--cigar-burn', physicalBurn.toFixed(4));
      this.ritual.style.setProperty('--ritual-orbit', `${orbit.toFixed(2)}deg`);
      this.ritual.style.setProperty('--cigar-ash-opacity', ashOpacity.toFixed(3));
      this.ritual.style.setProperty('--cigar-smoke-opacity', smoke.toFixed(3));
    }

    updateApproach(viewport) {
      if (!this.approach || !this.approachRows.length) return;

      const rect = this.approach.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, (viewport * 0.78 - rect.top) / Math.max(1, rect.height - viewport * 0.2)));
      this.approach.style.setProperty('--approach-progress', progress.toFixed(4));

      let closest = null;
      let closestDistance = Infinity;
      const targetY = viewport * 0.54;

      this.approachRows.forEach((row) => {
        const rowRect = row.getBoundingClientRect();
        const center = rowRect.top + rowRect.height / 2;
        const distance = Math.abs(center - targetY);
        if (distance < closestDistance) {
          closest = row;
          closestDistance = distance;
        }
      });

      this.approachRows.forEach((row) => row.classList.toggle('is-active', row === closest));
    }

    onPointerMove(event) {
      if (!this.hero) return;
      const rect = this.hero.getBoundingClientRect();
      const nx = ((event.clientX - rect.left) / Math.max(1, rect.width) - 0.5) * 2;
      const ny = ((event.clientY - rect.top) / Math.max(1, rect.height) - 0.5) * 2;

      if (this.pointerRaf) window.cancelAnimationFrame(this.pointerRaf);
      this.pointerRaf = window.requestAnimationFrame(() => {
        this.pointerRaf = 0;
        this.hero.style.setProperty('--hero-pointer-x', `${(nx * 9).toFixed(2)}px`);
        this.hero.style.setProperty('--hero-pointer-y', `${(ny * 6).toFixed(2)}px`);
        this.hero.style.setProperty('--hero-orbit-x', `${(nx * -5).toFixed(2)}px`);
        this.hero.style.setProperty('--hero-orbit-y', `${(ny * -4).toFixed(2)}px`);
      });
    }

    onPointerLeave() {
      if (!this.hero) return;
      this.hero.style.setProperty('--hero-pointer-x', '0px');
      this.hero.style.setProperty('--hero-pointer-y', '0px');
      this.hero.style.setProperty('--hero-orbit-x', '0px');
      this.hero.style.setProperty('--hero-orbit-y', '0px');
    }

    onResize() {
      this.measureRitualGeometry();
      this.onScroll();
    }

    onScroll() {
      if (this.raf) return;

      this.raf = window.requestAnimationFrame(() => {
        this.raf = 0;
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        this.updateHero(viewport);
        this.updateParallax(viewport);
        this.updateRitual(viewport);
        this.updateApproach(viewport);
      });
    }

    destroy() {
      this.revealObserver?.disconnect();
      this.ritualVisibilityObserver?.disconnect();
      this.rails.forEach((rail) => rail.destroy());
      this.ritualTabHandlers.forEach(([tab, click, keydown]) => {
        tab.removeEventListener('click', click);
        tab.removeEventListener('keydown', keydown);
      });
      this.ritualTabHandlers = [];
      if (this.timeTimer) window.clearInterval(this.timeTimer);
      this.hero?.removeEventListener('pointermove', this.onPointerMove);
      this.hero?.removeEventListener('pointerleave', this.onPointerLeave);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.raf) window.cancelAnimationFrame(this.raf);
      if (this.pointerRaf) window.cancelAnimationFrame(this.pointerRaf);
      controllers.delete(this.root);
    }
  }

  const init = (scope = document) => {
    const roots = scope.matches?.(SELECTOR) ? [scope] : [...scope.querySelectorAll?.(SELECTOR) || []];

    roots.forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new DeebAboutMotion(root));
    });
  };

  init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(SELECTOR)
      ? event.target
      : event.target.querySelector?.(SELECTOR);

    controllers.get(root)?.destroy();
  });
})();
